import { useState, useEffect, useCallback, type FormEvent } from "react";
import {
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import type { PaymentMethodsProps } from "@/interfaces";
import type { StripeElementChangeEvent, CardState } from "@/types";
import { useStripePayment } from "@/hooks/useStripePayment";

import PaymentMethodCardList from "@/components/paymentMethods/PaymentMethodsCardList";
import PaymentFooter from "@/components/paymentMethods/PaymentFooter";
import CardFormSection from "@/components/paymentMethods/CardFormSection";
import { validateEmail } from "@/lib/utils";
import Icons from "./Icons";
import { buildRedirectUrl } from "@/lib/redirect-utils";
import OneClickPaymentFooter from "./paymentMethods/OneClickPaymentFooter";
import PaymentErrorModal from "./PaymentErrorModal";
import { Button } from "./ui/button";
import { addSavedPaymentMethodId } from "@/lib/saved-payment-methods-storage";
import { getDecodedTokenFromSource } from "@/lib/token-utils";
import { useStyleTheme } from "@/hooks/useStyleTheme";
import { useAnalytics } from "@/hooks/useAnalytics";
import {
  getCheckoutAnalyticsData,
  storeCheckoutAnalyticsData,
  safeTrack,
} from "@/services/AnalyticsService";

const PaymentMethods = ({
  activeButton,
  showNav,
  setShowNav,
  savedCards,
  email,
  setEmail,
  emailError,
  setEmailError,
  onConfirm,
  onSavedCardsClick,
  showApplePay,
  showGooglePay,
  isPaymentMethodsLoading,
  clientSecret,
  sessionId,
  gameData,
  isSkeletonPhase = false,
  hasExpressLoadedOnce = false,
  onExpressLoaded,
  isExpressReinitializing = false,
  onExpressReinitialized,
  paypalEnabled = true,
  optimisticGpay = false,
}: PaymentMethodsProps) => {
  // Filter out PayPal methods - they can't be processed via Stripe's confirmCardPayment
  // PayPal payments are handled separately through SavedPayPalButton
  const creditCardsOnly = savedCards.filter(card => !card.id.startsWith('paypal_vaulted_'));
  const theme = useStyleTheme(gameData);
  const { track, isEnabled } = useAnalytics();
  
  const [showAddCard, setShowAddCard] = useState(false);
  const [showCardDropdown, setShowCardDropdown] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [cardState, setCardState] = useState<CardState>({
    number: false,
    expiry: false,
    cvc: false,
  });
  const { processCardPayment } = useStripePayment();
  const trackPurchaseFailed = useCallback(
    (reason: string, paymentMethod: string) => {
      if (!isEnabled) return;
      const analyticsData = getCheckoutAnalyticsData();
      safeTrack(track, 'purchase_failed', {
        reason,
        payment_method: paymentMethod,
        ...analyticsData,
      });
    },
    [isEnabled, track]
  );
  const stripe = useStripe();
  const elements = useElements();
  const [lastSelectedCardId, setLastSelectedCardId] = useState<string | null>(
    () => {
      // Initialize from localStorage if available
      try {
        return localStorage.getItem('lastSelectedCardId');
      } catch {
        return null;
      }
    }
  );
  const [explicitlySelectedMethod, setExplicitlySelectedMethod] = useState<
    "card" | "applePay" | "addCard" | null
  >(null);
  const [saveCard, setSaveCard] = useState(false);

  // Helper function to update lastSelectedCardId and persist to localStorage
  const updateLastSelectedCardId = useCallback((cardId: string | null) => {
    setLastSelectedCardId(cardId);
    try {
      if (cardId) {
        localStorage.setItem('lastSelectedCardId', cardId);
      } else {
        localStorage.removeItem('lastSelectedCardId');
      }
    } catch (error) {
      console.error('Failed to update localStorage:', error);
    }
  }, []);

  // Reset showAddCard when navigating back to main page (showNav becomes true)
  useEffect(() => {
    if (showNav && showAddCard) {
      setShowAddCard(false);
      // Restore the previously selected card or default to first card
      if (creditCardsOnly.length > 0) {
        // Try to restore the last selected card if it still exists
        const lastCardStillExists = lastSelectedCardId && creditCardsOnly.some(card => card.id === lastSelectedCardId);
        if (lastCardStillExists) {
          setSelectedCardId(lastSelectedCardId);
        } else {
          setSelectedCardId(creditCardsOnly[0]?.id || null);
        }
      } else {
        setSelectedCardId(null);
      }
    }
  }, [showNav, showAddCard, creditCardsOnly, lastSelectedCardId]);

  useEffect(() => {
    if (explicitlySelectedMethod === "card") {
      setShowAddCard(false);
      if (lastSelectedCardId) {
        // Check if the last selected card still exists in the credit cards (not PayPal)
        const cardExists = creditCardsOnly.some(card => card.id === lastSelectedCardId);
        if (cardExists) {
          setSelectedCardId(lastSelectedCardId);
        } else {
          // If the last selected card no longer exists, clear it and use the first card
          updateLastSelectedCardId(null);
          setSelectedCardId(creditCardsOnly[0]?.id || null);
        }
      } else if (creditCardsOnly.length > 0) {
        setSelectedCardId(creditCardsOnly[0]?.id || null);
      }
      return;
    }
    if (explicitlySelectedMethod === "addCard") {
      setShowAddCard(true);
      setSelectedCardId(null);
      return;
    }

    if (creditCardsOnly.length > 0) {
      setShowAddCard(false);
      
      // Check if the last selected card still exists
      if (lastSelectedCardId && creditCardsOnly.some(card => card.id === lastSelectedCardId)) {
        setSelectedCardId((prev) => prev || lastSelectedCardId);
      } else {
        // If last selected card doesn't exist, clear it and use the first card (most recent)
        if (lastSelectedCardId) {
          updateLastSelectedCardId(null);
        }
        setSelectedCardId((prev) => prev || creditCardsOnly[0]?.id || null);
      }
    } else {
      // No saved credit cards
      // For Credit Card tab: show card selection interface (not form)
      // For One Click tab: would show "Add A New Card" button
      setShowAddCard(false);
      setSelectedCardId(null);
    }
  }, [
    creditCardsOnly,
    showApplePay,
    showGooglePay,
    lastSelectedCardId,
    explicitlySelectedMethod,
    updateLastSelectedCardId,
  ]);

  const handleSavedCardSelect = useCallback((cardId: string, selected: boolean) => {
    if (selected) {
      setSelectedCardId(cardId);
      updateLastSelectedCardId(cardId);
      setShowAddCard(false);
      setCardState({ number: false, expiry: false, cvc: false });
      setExplicitlySelectedMethod("card");
      setPaymentError(null); // Clear error when selecting a different card
    } else {
      setSelectedCardId(null);
      setExplicitlySelectedMethod(null);
    }
  }, [updateLastSelectedCardId]);

  const handleElementChange = useCallback((event: StripeElementChangeEvent) => {
    const elementType = event.elementType;
    const isComplete = event.complete;
    const isEmpty = event.empty;
    if (!isEmpty && selectedCardId) {
      setSelectedCardId(null);
    }
    setCardState((prev) => ({
      ...prev,
      [elementType === "cardNumber"
        ? "number"
        : elementType === "cardExpiry"
        ? "expiry"
        : "cvc"]: isComplete,
    }));
  }, [selectedCardId]);

  const isCardComplete = cardState.number && cardState.expiry && cardState.cvc;
  const isEmailValid = validateEmail(email);
  const isEmailProvided = email.trim() !== "";
  const isSubmitDisabled =
    isLoading ||
    (showAddCard
      ? !isCardComplete || !isEmailProvided || !isEmailValid  // Adding new card: require all fields
      : selectedCardId
      ? false  // Using saved card: enable button
      : true); // No card selected: disable button

  const processSavedCardPayment = async (cardId: string) => {
    if (!stripe) {
      setPaymentError("Stripe not initialized");
      return;
    }

    // Only allow credit cards, not PayPal methods
    const selectedCard = creditCardsOnly.find((card) => card.id === cardId);
    if (!selectedCard) {
      setPaymentError("Card not found");
      return;
    }

    // Update last selected card ID before payment since this card is being used
    updateLastSelectedCardId(cardId);

    storeCheckoutAnalyticsData({ ...getCheckoutAnalyticsData(), payment_method: 'card' });

    try {
      const { paymentIntent, error } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: selectedCard.id,
        // Only assign receipt_email if not explicitly disabled in game config (defaults to true)
        ...(gameData.assign_receipt_email !== false && email && { receipt_email: email })
      });

      if (error) {
        // Use Stripe's error message directly - they're already user-friendly
        const errorMessage = error.message || "Payment failed. Please try again or use a different payment method.";
        setPaymentError(errorMessage);
        trackPurchaseFailed(errorMessage, 'card');
        return;
      }

      if (paymentIntent) {
        switch (paymentIntent.status) {
          case "succeeded":
            window.location.href = buildRedirectUrl("success");
            break;
          case "requires_action":
            if (paymentIntent.next_action?.type === "use_stripe_sdk") {
              const { error: confirmError } = await stripe.confirmCardPayment(
                clientSecret
              );
              if (confirmError) {
                const errorMessage = confirmError.message || "Authentication failed. Please try again.";
                trackPurchaseFailed(errorMessage, 'card');
                setPaymentError(
                  errorMessage
                );
              } else {
                window.location.href = buildRedirectUrl("success");
              }
            } else {
              const errorMessage =
                "Additional authentication required. Please try again.";
              trackPurchaseFailed(errorMessage, 'card');
              setPaymentError(errorMessage);
            }
            break;
          case "processing":
            window.location.href = buildRedirectUrl("success");
            break;
          case "requires_payment_method":
            trackPurchaseFailed(
              "Payment failed. Please try another payment method.",
              'card'
            );
            setPaymentError("Payment failed. Please try another payment method.");
            break;
          default:
            trackPurchaseFailed(
              "Payment could not be completed. Please try again or use a different payment method.",
              'card'
            );
            setPaymentError(
              "Payment could not be completed. Please try again or use a different payment method."
            );
        }
      }
    } catch (err) {
      console.error("Payment error:", err);
      trackPurchaseFailed(
        "An unexpected error occurred. Please try again.",
        'card'
      );
      setPaymentError("An unexpected error occurred. Please try again.");
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    try {
      track('click_in_checkout', { cta_name: 'pay', payment_method: 'card' });
    } catch {
      // Ignore tracking errors
    }
    
    setIsLoading(true);
    setPaymentError(null);
    try {
      if (selectedCardId) {
        await processSavedCardPayment(selectedCardId);
        return;
      }
      if (!stripe || !elements) {
        setPaymentError("Stripe or Elements not initialized");
        return;
      }

      storeCheckoutAnalyticsData({ ...getCheckoutAnalyticsData(), payment_method: 'new_card' });

      // Extract payment intent ID from client secret (format: pi_xxxxx_secret_yyyy)
      const sessionId = clientSecret.split('_secret_')[0];
      
      const result = await processCardPayment(
        stripe,
        elements,
        clientSecret,
        email,
        saveCard,
        sessionId
      );
      if (result.success && result.redirectUrl) {
        // If card was saved and payment succeeded, update the last selected card ID
        if (saveCard && result.paymentMethodId) {
          updateLastSelectedCardId(result.paymentMethodId);
          
          // Store the payment method ID in localStorage for client-saved payment methods feature
          const decodedToken = getDecodedTokenFromSource('any');
          if (decodedToken?.appId && decodedToken?.stripeCustomerId) {
            addSavedPaymentMethodId(
              decodedToken.appId,
              decodedToken.stripeCustomerId as string,
              result.paymentMethodId
            );
          }

          if (isEnabled) {
            safeTrack(track, 'payment_method_updated', {});
          }
        }
        window.location.href = result.redirectUrl;
      } else {
        // Show error inline instead of redirecting
        const errorMessage =
          result.error ||
          "Payment failed. Please try again or use a different payment method.";
        trackPurchaseFailed(errorMessage, 'card');
        setPaymentError(errorMessage);
      }
    } catch {
      trackPurchaseFailed(
        "An unexpected error occurred. Please try again.",
        'card'
      );
      setPaymentError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardPayClick = async (): Promise<void> => {
    try {
      track('click_in_checkout', { cta_name: 'pay', payment_method: 'card' });
    } catch {
      // Ignore tracking errors
    }
    setIsLoading(true);
    setPaymentError(null);
    try {
      if (selectedCardId) {
        await processSavedCardPayment(selectedCardId);
      }
    } catch {
      setPaymentError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-0 flex-1">
      {/* Payment Error Modal */}
      <PaymentErrorModal
        isOpen={!!paymentError}
        errorMessage={paymentError || ""}
        onClose={() => setPaymentError(null)}
        onTryDifferentMethod={() => {
          setPaymentError(null);
          // Smart logic for "Try Different Method" - only consider credit cards, not PayPal
          if (creditCardsOnly.length > 1 && selectedCardId) {
            // Switch to a different saved credit card
            const otherCards = creditCardsOnly.filter(card => card.id !== selectedCardId);
            if (otherCards.length > 0) {
              handleSavedCardSelect(otherCards[0].id, true);
              return;
            }
          }
          // Fallback to "Add New Card"
          setShowNav?.(false);
          setShowAddCard(true);
          setSelectedCardId(null);
          setExplicitlySelectedMethod("addCard");
        }}
      />
      
      {activeButton === "One Click" ? (
        <>
          {!showAddCard ? (
            <div className=" mx-5 flex flex-col flex-1 min-h-0 h-full justify-between">
                        <OneClickPaymentFooter
            isLoading={isLoading}
            onConfirm={onConfirm}
            savedCards={savedCards}
            selectedCardId={selectedCardId}
            lastSelectedCardId={lastSelectedCardId}
            handleCardPayClick={handleCardPayClick}
            onAddNewCard={() => {
              try {
                track('click_in_checkout', { cta_name: 'add new card' });
              } catch {
                // Ignore tracking errors
              }
              setShowNav?.(false);
              setShowAddCard(true);
              setSelectedCardId(null);
              setExplicitlySelectedMethod("addCard");
              setPaymentError(null); // Clear error when switching to add new card
            }}
            onSavedCardSelect={handleSavedCardSelect}
            sessionId={sessionId}
            gameData={gameData}
            isSkeletonPhase={isSkeletonPhase}
            hasExpressLoadedOnce={hasExpressLoadedOnce}
            onExpressLoaded={onExpressLoaded}
            isExpressReinitializing={isExpressReinitializing}
            onExpressReinitialized={onExpressReinitialized}
            paypalEnabled={paypalEnabled}
            optimisticGpay={optimisticGpay}
          />
            </div>
          ) : (
            <CardFormSection
              handleSubmit={handleSubmit}
              savedCards={savedCards}
              showApplePay={showApplePay}
              showGooglePay={showGooglePay}
              email={email}
              setEmail={setEmail}
              emailError={emailError}
              setEmailError={setEmailError}
              isSubmitDisabled={isSubmitDisabled}
              isLoading={isLoading}
              handleElementChange={handleElementChange}
              onBack={() => {
                setShowNav?.(true);
                setShowAddCard(false);
                setExplicitlySelectedMethod(null);
                setPaymentError(null); // Clear error when going back
              }}
              saveCard={saveCard}
              setSaveCard={setSaveCard}
              isOneClickContext={activeButton === "One Click"}
            />
          )}
        </>
      ) : (
        <>
          {!showAddCard && (
            <div className="flex flex-col flex-1 min-h-0 h-full justify-between">
              <PaymentMethodCardList
                savedCards={savedCards}
                selectedCardId={selectedCardId}
                lastSelectedCardId={lastSelectedCardId}
                isLoading={isPaymentMethodsLoading}
                showCardDropdown={showCardDropdown}
                setShowCardDropdown={setShowCardDropdown}
                handleSavedCardSelect={handleSavedCardSelect}
                theme={theme}
              />
              {/* <ApplePayOption
                showApplePay={showApplePay}
                showGooglePay={showGooglePay}
                applePay={applePay}
                isLoading={isPaymentMethodsLoading}
                handleApplePayToggle={handleApplePayToggle}
              /> */}

              <Button className="ml-5 mr-3 mb-2 flex items-center justify-between" onClick={() => {
                  try {
                    track('click_in_checkout', { cta_name: 'add new card' });
                  } catch {
                    // Ignore tracking errors
                  }
                  setShowNav?.(false);
                  setShowAddCard(true);
                  setSelectedCardId(null);
                  setExplicitlySelectedMethod("addCard");
                  setPaymentError(null); // Clear error when switching to add new card
                }}>
                  
                <span className="text-[13px] text-[#212121] font-normal font-['Poppins'] leading-[normal] text-edge-cap leading-trim-both">Add New Card</span>
                <Icons type="chevronRight" size={24} />
              </Button>

              <PaymentFooter
                isLoading={isLoading}
                selectedCardId={selectedCardId}
                paymentError={paymentError}
                handleCardPayClick={handleCardPayClick}
                onSavedCardsClick={onSavedCardsClick}
                savedCards={savedCards}
                onAddNewCard={() => {
                  try {
                    track('click_in_checkout', { cta_name: 'add new card' });
                  } catch {
                    // Ignore tracking errors
                  }
                  setShowNav?.(false);
                  setShowAddCard(true);
                  setSelectedCardId(null);
                  setExplicitlySelectedMethod("addCard");
                  setPaymentError(null); // Clear error when switching to add new card
                }}
                gameData={gameData}
              />
            </div>
          )}
          {showAddCard && (
            <CardFormSection
              handleSubmit={handleSubmit}
              savedCards={savedCards}
              showApplePay={showApplePay}
              showGooglePay={showGooglePay}
              email={email}
              setEmail={setEmail}
              emailError={emailError}
              setEmailError={setEmailError}
              isSubmitDisabled={isSubmitDisabled}
              isLoading={isLoading}
              handleElementChange={handleElementChange}
              onBack={() => {
                setShowNav?.(true);
                setShowAddCard(false);
                setExplicitlySelectedMethod(null);
                if (creditCardsOnly.length > 0) {
                  setSelectedCardId(creditCardsOnly[0]?.id || null);
                } else {
                  setSelectedCardId(null);
                }
              }}
              saveCard={saveCard}
              setSaveCard={setSaveCard}
              isOneClickContext={false}
            />
          )}
        </>
      )}
    </div>
  );
};

export default PaymentMethods;
